/* eslint-disable react-hooks/refs */
import { type LatLng, type NearbyLocation } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Pressable, ScrollView, View } from 'react-native'
import { useSpinDelay } from 'spin-delay'
import { useDebounce } from 'use-debounce'
import { toLocationKey } from '~/components/locations/utils'
import FloatingPopover from '~/components/ui/floating-popover'
import { Input, type InputVariant } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'
import { cn } from '~/lib/utils'

const categoryPriority: Record<string, number> = {
  suburb: 1,
  quarter: 2,
  borough: 3,
  neighbourhood: 4,
  city_district: 5,
}

const MIN_CHARS = 3
const MAX_RESULTS = 10

export function LocationAutocomplete({
  bias,
  onSelect,
  countryCode,
  placeholder,
  className,
  inputClassName,
  disabled,
  inputVariant,
  testIdBase,
}: {
  bias: LatLng | null
  onSelect: (location: NearbyLocation) => void
  countryCode?: string
  placeholder?: string
  className?: string
  inputClassName?: string
  disabled?: boolean
  inputVariant?: InputVariant
  testIdBase?: string
}) {
  const { t } = useTranslations('common')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query.trim(), 400)
  const blurTimer = useRef<NodeJS.Timeout | null>(null)
  const isInteractingWithPopover = useRef(false)

  const cancelBlurTimer = useCallback(() => {
    if (blurTimer.current) {
      clearTimeout(blurTimer.current)
      blurTimer.current = null
    }
  }, [])

  const rawLen = useMemo(() => query.trim().length, [query])
  const canSearch = open && (debouncedQuery?.length ?? 0) >= MIN_CHARS

  const { data: results = [], isLoading } = useQuery(
    trpc.locations.autocomplete.queryOptions(
      {
        text: debouncedQuery || '',
        bias: bias || { lat: 60.1708, lng: 24.9375 },
        countryCode: countryCode || 'fi',
        limit: 30,
      },
      {
        enabled: canSearch,
        staleTime: 1000 * 60 * 5,
        placeholderData: keepPreviousData,
      }
    )
  )

  const prevResults = useRef<NearbyLocation[] | null>(null)

  useEffect(() => {
    if (results.length > 0) {
      prevResults.current = results
    }
  }, [results])

  const hasMinChars = rawLen >= MIN_CHARS

  const preMinCharsLoading = open && rawLen > 0 && !hasMinChars
  const fetchLoadingRaw = isLoading && hasMinChars

  const showLoading = preMinCharsLoading || fetchLoadingRaw

  const displayedResults = useMemo(
    () => (fetchLoadingRaw && results.length === 0 ? prevResults.current || [] : results),
    [fetchLoadingRaw, results]
  )

  const filteredResults = useMemo(() => {
    if (!displayedResults) return []

    const postalCodeGroups: Record<string, NearbyLocation> = {}

    for (const item of displayedResults) {
      const existing = postalCodeGroups[item.postalCode]

      if (!existing) {
        postalCodeGroups[item.postalCode] = item
        continue
      }

      const existingPriority = categoryPriority[existing.metadata?.providerCategory ?? ''] ?? 999
      const itemPriority = categoryPriority[item.metadata?.providerCategory ?? ''] ?? 999

      if (itemPriority < existingPriority) {
        postalCodeGroups[item.postalCode] = item
      }
    }

    const filteredResults = Object.values(postalCodeGroups).sort(
      (a, b) => (a.distance ?? 0) - (b.distance ?? 0)
    )

    return filteredResults.slice(0, MAX_RESULTS) as NearbyLocation[]
  }, [displayedResults])

  const hasResults = filteredResults.length > 0

  const rawEmpty = hasMinChars && !fetchLoadingRaw && !hasResults
  const showEmpty = useSpinDelay(rawEmpty, { delay: 200, minDuration: 400 })

  return (
    <View className={cn('relative z-50 w-full flex-1', className)}>
      <FloatingPopover
        isOpen={open && !disabled && (showLoading || hasResults || showEmpty)}
        offset={6}
        contentClassName="rounded-lg bg-background max-h-[300px] gap-1"
        trigger={() => (
          <Input
            testID={testIdBase ? `${testIdBase}-input` : undefined}
            placeholder={placeholder ?? 'Etsi aluetta (kaupunginosa, alue, kaupunginosa...)'}
            value={query}
            onChangeText={(text) => {
              setQuery(text)
              if (!open) setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              cancelBlurTimer()
              blurTimer.current = setTimeout(() => {
                if (!isInteractingWithPopover.current) {
                  setOpen(false)
                }
              }, 150)
            }}
            editable={!disabled}
            className={cn('w-full', inputClassName)}
            clearTextOnFocus
            showClearButton
            variant={inputVariant}
          />
        )}>
        <View
          className="flex-1"
          onTouchStart={() => {
            isInteractingWithPopover.current = true
            cancelBlurTimer()
          }}
          onTouchEnd={() => {
            isInteractingWithPopover.current = false
          }}>
          {showLoading && (
            <View className="flex-row items-center gap-2 p-3">
              <ActivityIndicator size="small" />
              <Text>{t(($) => $.fileUpload.upload ?? 'Ladataan...')}</Text>
            </View>
          )}

          {!showLoading && hasResults && (
            <ScrollView
              className="max-h-[250px] w-full"
              contentContainerClassName="p-1.5"
              showsVerticalScrollIndicator
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled>
              {filteredResults.map((item) => (
                <Pressable
                  testID={testIdBase ? `${testIdBase}-result-${toLocationKey(item)}` : undefined}
                  key={toLocationKey(item)}
                  className="group/item flex-row flex-wrap items-baseline justify-start gap-2 rounded-md bg-background px-4 py-3 transition-transform duration-150 hover:bg-accent active:scale-95"
                  onPress={() => {
                    isInteractingWithPopover.current = false
                    onSelect({ ...item, distance: item.distance })
                    setOpen(false)
                    setQuery('')
                  }}>
                  <Text
                    numberOfLines={1}
                    className="whitespace-nowrap text-left text-lg text-primary group-hover/item:text-accent-foreground">
                    {item.label}
                  </Text>
                  <View className="flex-row items-center gap-2">
                    {!!item.postalCode && (
                      <Text className="text-sm text-secondary group-hover/item:text-accent-foreground">
                        {item.postalCode}
                      </Text>
                    )}
                    {!!item.discriminator && (
                      <Text className="text-sm text-secondary group-hover/item:text-accent-foreground">
                        {item.discriminator}
                      </Text>
                    )}
                  </View>
                </Pressable>
              ))}
            </ScrollView>
          )}

          {!showLoading && !hasResults && showEmpty && (
            <View className="p-3">
              <Text className="text-secondary">
                {t(($) => $.notFound.message ?? 'Ei hakutuloksia.')}
              </Text>
            </View>
          )}
        </View>
      </FloatingPopover>
    </View>
  )
}
